import { motion } from "framer-motion";
import { MapPin, Radar, Users2, Music2 } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const FEATURES = [
  {
    icon: MapPin,
    tag: "Live Map",
    title: "See the music around you, right now.",
    body:
      "Every pin on the map is a real person, playing a real song, this second. Tap in to see the track, the artist, and how close they are.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/8ac86e8d-d508-42f4-a992-5d8485e6789c/images/9c72facc449e80b13731688bbaa137ccaefd247ff442e8c5407170749e3a2797.jpeg",
  },
  {
    icon: Radar,
    tag: "Song Radar",
    title: "Don't know what's playing? Point and ask.",
    body:
      "Hear something great in a café or a car passing by? Song Radar listens for a few seconds and identifies it instantly — no typing, no guessing.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/8ac86e8d-d508-42f4-a992-5d8485e6789c/images/3ee0e3a55f7b2312b85497b1f2946824dc724dd0313a3709125ce7bf8e98c78d.jpeg",
  },
  {
    icon: Users2,
    tag: "Listen Along",
    title: "Sync up with anyone, anywhere.",
    body:
      "Jump into someone's session and hear exactly what they're hearing, in perfect time — whether they're next to you or across the city.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/8ac86e8d-d508-42f4-a992-5d8485e6789c/images/cb1d069c564253160b8cf9a910528688d5178c51392642f2235f98a504c83c3f.jpeg",
  },
];

const rowVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const Features = () => {
  return (
    <section id="features" className="relative bg-[#07030f] py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-xl mb-20 sm:mb-28"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#22d3ee] font-semibold">
            What you get
          </span>
          <h2
            data-testid={LANDING.featuresHeading}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 leading-tight"
          >
            One app, three ways to feel the room.
          </h2>
        </motion.div>

        <div className="space-y-28 sm:space-y-36">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            const reversed = i % 2 === 1;
            return (
              <motion.div
                key={f.tag}
                data-testid={LANDING.featureRow(i)}
                variants={rowVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-120px" }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  reversed ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-[#a259ff]/30 bg-[#a259ff]/10 px-4 py-1.5 mb-6">
                    <Icon className="h-3.5 w-3.5 text-[#22d3ee]" strokeWidth={2.5} />
                    <span className="text-xs font-semibold tracking-wide text-[#e6d1ff]">
                      {f.tag}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight max-w-md">
                    {f.title}
                  </h3>
                  <p className="mt-5 text-[#b9a9e6] leading-relaxed max-w-md">
                    {f.body}
                  </p>
                </div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.4 }}
                  className="relative rounded-3xl overflow-hidden border border-white/[0.08] shadow-[0_20px_60px_-15px_rgba(162,89,255,0.35)]"
                >
                  <img
                    src={f.image}
                    alt={f.title}
                    className="w-full h-full object-cover aspect-[3/2]"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#07030f]/40 via-transparent to-transparent" />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
